import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Minus, Trash2, ShoppingCart, UtensilsCrossed, ChefHat, Coffee, Send, StickyNote, CreditCard, Banknote, Receipt, X, Clock, FileText, Printer, ArrowRightLeft, Scissors, Check, Package, Phone } from 'lucide-react';
import axios from 'axios';
import QRCode from 'qrcode';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

import { calculateCateringTotal, getCateringBreakdown, BASE_PRICE } from '../utils/pricing';
import WaiterCallsBanner from '../components/WaiterCallsBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_TABLES = 12;

const CATERING_CATEGORIES = [
  { key: 'corbalar', label: 'Corbalar' },
  { key: 'ana_yemekler', label: 'Ana Yemekler' },
  { key: 'yardimci_yemekler', label: 'Yardimci' },
  { key: 'salatalar', label: 'Salatalar' },
];

const Waiter = () => {
  const { token, isAdmin } = useAuth();
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableOrders, setTableOrders] = useState({});
  const [tableDetailOrders, setTableDetailOrders] = useState([]);
  const [tableDetailTotal, setTableDetailTotal] = useState(0);
  const [viewMode, setViewMode] = useState('login'); // 'login' | 'tables' | 'detail' | 'neworder'
  const [menuItems, setMenuItems] = useState([]);
  const [beverageItems, setBeverageItems] = useState([]);
  const [cateringMenu, setCateringMenu] = useState(null);
  const [cart, setCart] = useState([]);
  const [notes, setNotes] = useState('');
  const [payment, setPayment] = useState('nakit');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('alakart');
  const [loading, setLoading] = useState(false);
  const [closingTable, setClosingTable] = useState(false);
  const [cateringQty, setCateringQty] = useState({});
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [splitGroups, setSplitGroups] = useState([[], []]);
  const [splitMode, setSplitMode] = useState('item'); // 'item' | 'equal'
  const [splitPersonCount, setSplitPersonCount] = useState(2);
  const [pinInput, setPinInput] = useState('');
  const [currentWaiter, setCurrentWaiter] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [orderMode, setOrderMode] = useState('table'); // 'table' | 'paket'
  const [paketPhone, setPaketPhone] = useState('');

  const fetchTableOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/tables`, { headers: { Authorization: `Bearer ${token}` } });
      setTableOrders(res.data);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchTableOrders();
    axios.get(`${API}/menu`).then(r => setMenuItems(r.data)).catch(() => {});
    axios.get(`${API}/beverages`).then(r => setBeverageItems(r.data)).catch(() => {});
    axios.get(`${API}/catering/today`).then(r => { if (r.data) setCateringMenu(r.data); }).catch(() => {});
    const interval = setInterval(fetchTableOrders, 15000);
    return () => clearInterval(interval);
  }, [token, fetchTableOrders]);

  const fetchTableDetail = useCallback(async (tableNum) => {
    try {
      const res = await axios.get(`${API}/orders/table/${tableNum}/active`, { headers: { Authorization: `Bearer ${token}` } });
      setTableDetailOrders(res.data.orders || []);
      setTableDetailTotal(res.data.total_price || 0);
    } catch {
      setTableDetailOrders([]);
      setTableDetailTotal(0);
    }
  }, [token]);

  const handlePinLogin = async () => {
    if (!pinInput.trim()) return;
    setPinLoading(true);
    try {
      const res = await axios.post(`${API}/waiter/login`, { pin: pinInput });
      setCurrentWaiter(res.data);
      setViewMode('tables');
      toast.success(`Hosgeldin ${res.data.name}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gecersiz PIN');
    } finally { setPinLoading(false); }
  };

  const handleWaiterLogout = () => {
    setCurrentWaiter(null);
    setPinInput('');
    setViewMode('login');
    setSelectedTable(null);
  };

  const handleTableClick = async (num) => {
    setSelectedTable(num);
    setOrderMode('table');
    const hasOrder = !!tableOrders[num];
    if (hasOrder) {
      setViewMode('detail');
      await fetchTableDetail(num);
    } else {
      setViewMode('neworder');
    }
  };

  const handleBack = () => {
    if (viewMode === 'neworder' && orderMode === 'table' && tableOrders[selectedTable]) {
      setViewMode('detail');
      setCart([]);
      setNotes('');
      setCateringQty({});
    } else {
      setSelectedTable(null);
      setOrderMode('table');
      setViewMode('tables');
      setCart([]);
      setNotes('');
      setPaketPhone('');
      setCateringQty({});
      setTableDetailOrders([]);
    }
  };

  const handleCloseTable = async () => {
    if (!confirm(`Masa ${selectedTable} hesabi kapatilsin mi?`)) return;
    setClosingTable(true);
    try {
      await axios.put(`${API}/orders/table/${selectedTable}/close`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Masa ${selectedTable} hesabi kapatildi!`);
      setSelectedTable(null);
      setViewMode('tables');
      setTableDetailOrders([]);
      fetchTableOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hesap kapatilamadi');
    } finally { setClosingTable(false); }
  };

  const handleTransfer = async (newTable) => {
    setTransferring(true);
    try {
      await axios.put(`${API}/orders/table/${selectedTable}/transfer/${newTable}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Masa ${selectedTable} -> Masa ${newTable} transfer edildi!`);
      setShowTransfer(false);
      setSelectedTable(null);
      setViewMode('tables');
      setTableDetailOrders([]);
      fetchTableOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer yapilamadi');
    } finally { setTransferring(false); }
  };

  const printReceipt = () => {
    const allItems = tableDetailOrders.flatMap(o => o.items || []).reduce((acc, item) => {
      const existing = acc.find(a => a.name === item.name);
      if (existing) { existing.quantity += item.quantity; existing.total += item.price * item.quantity; }
      else acc.push({ name: item.name, quantity: item.quantity, price: item.price, total: item.price * item.quantity });
      return acc;
    }, []);
    const itemsHtml = allItems.map(i => `<div class="itm"><div class="ir"><span class="n">${i.quantity}x</span><span class="nm">${i.name}</span><span class="pr">\u20AC${i.total.toFixed(2)}</span></div></div>`).join('');
    const now = new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Masa ${selectedTable}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;width:80mm;max-width:302px;padding:4px 6px;font-size:12px;line-height:1.3;color:#000}
.hdr{text-align:center;padding:6px 0;border-bottom:2px solid #000}.hdr h1{font-size:16px;font-weight:900;letter-spacing:1px}
.onum{text-align:center;font-size:24px;font-weight:900;padding:6px 0;margin:4px 0;border:2px solid #000;background:#f0f0f0}
.nfo{padding:4px 0;border-bottom:1px solid #999;font-size:11px}.nfo div{display:flex;justify-content:space-between;margin:2px 0}.nfo b{font-weight:800}
.stit{font-weight:900;font-size:13px;padding:5px 0 3px;border-bottom:2px solid #000;text-transform:uppercase}
.itm{padding:4px 0;border-bottom:1px dotted #999}.ir{display:flex;align-items:center;gap:4px;font-size:12px}
.ir .n{font-weight:800;min-width:20px;font-size:14px}.ir .nm{flex:1;font-weight:700;font-size:13px}.ir .pr{font-weight:900;font-size:13px}
.tot{display:flex;justify-content:space-between;font-size:20px;font-weight:900;padding:6px 0;margin:4px 0;border-top:2px solid #000;border-bottom:2px solid #000}
.ft{text-align:center;padding:5px 0;margin-top:4px;border-top:1px dashed #999;font-size:10px}.ft b{font-size:12px;display:block;margin-bottom:2px}
@media print{body{width:80mm}@page{margin:0;size:80mm auto}}</style></head>
<body><div class="hdr"><h1>CENTRAL RESTAURANT</h1></div>
<div class="onum">MASA ${selectedTable} - ADISYON</div>
<div class="nfo"><div><span>TARIH:</span><b>${now}</b></div><div><span>SIPARIS:</span><b>${tableDetailOrders.length} adet</b></div></div>
<div class="stit">URUNLER</div>${itemsHtml}
<div class="tot"><span>TOPLAM</span><span>\u20AC${tableDetailTotal.toFixed(2)}</span></div>
<div class="ft"><b>AFIYET OLSUN!</b>Central Hotel | +382 69 144 043</div></body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    const tryPrint = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch(e) {} setTimeout(() => { try { document.body.removeChild(iframe); } catch(e) {} }, 3000); };
    if (iframe.contentWindow.document.readyState === 'complete') setTimeout(tryPrint, 300);
    else iframe.onload = () => setTimeout(tryPrint, 300);
    toast.success('Adisyon yazdiriliyor...');
  };

  const printSplitReceipt = (items, groupIndex, total) => {
    const itemsHtml = items.map(i => `<div class="itm"><div class="ir"><span class="n">${i.quantity}x</span><span class="nm">${i.name_tr || i.name}</span><span class="pr">\u20AC${(i.price * i.quantity).toFixed(2)}</span></div></div>`).join('');
    const now = new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Masa ${selectedTable} - Grup ${groupIndex + 1}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Courier New',monospace;width:80mm;max-width:302px;padding:4px 6px;font-size:12px;line-height:1.3;color:#000}
.hdr{text-align:center;padding:6px 0;border-bottom:2px solid #000}.hdr h1{font-size:16px;font-weight:900}
.onum{text-align:center;font-size:20px;font-weight:900;padding:6px 0;margin:4px 0;border:2px solid #000;background:#f0f0f0}
.nfo{padding:4px 0;border-bottom:1px solid #999;font-size:11px}.nfo div{display:flex;justify-content:space-between;margin:2px 0}.nfo b{font-weight:800}
.stit{font-weight:900;font-size:13px;padding:5px 0 3px;border-bottom:2px solid #000}
.itm{padding:4px 0;border-bottom:1px dotted #999}.ir{display:flex;align-items:center;gap:4px;font-size:12px}
.ir .n{font-weight:800;min-width:20px;font-size:14px}.ir .nm{flex:1;font-weight:700;font-size:13px}.ir .pr{font-weight:900;font-size:13px}
.tot{display:flex;justify-content:space-between;font-size:20px;font-weight:900;padding:6px 0;margin:4px 0;border-top:2px solid #000;border-bottom:2px solid #000}
.ft{text-align:center;padding:5px 0;margin-top:4px;border-top:1px dashed #999;font-size:10px}.ft b{font-size:12px;display:block;margin-bottom:2px}
@media print{body{width:80mm}@page{margin:0;size:80mm auto}}</style></head>
<body><div class="hdr"><h1>CENTRAL RESTAURANT</h1></div>
<div class="onum">MASA ${selectedTable} - HESAP ${groupIndex + 1}</div>
<div class="nfo"><div><span>TARIH:</span><b>${now}</b></div>${currentWaiter ? `<div><span>GARSON:</span><b>${currentWaiter.name}</b></div>` : ''}</div>
<div class="stit">URUNLER</div>${itemsHtml}
<div class="tot"><span>TOPLAM</span><span>\u20AC${total.toFixed(2)}</span></div>
<div class="ft"><b>AFIYET OLSUN!</b>Central Hotel | +382 69 144 043</div></body></html>`;
    printHtmlContent(html);
  };

  const printEqualSplitReceipt = (personNum, totalPersons, total) => {
    const perPerson = total / totalPersons;
    const allItems = tableDetailOrders.flatMap(o => o.items || []);
    const itemsHtml = allItems.map(i => `<div class="itm"><div class="ir"><span class="n">${i.quantity}x</span><span class="nm">${i.name_tr || i.name}</span><span class="pr">\u20AC${(i.price * i.quantity).toFixed(2)}</span></div></div>`).join('');
    const now = new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Masa ${selectedTable} - Kisi ${personNum}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Courier New',monospace;width:80mm;max-width:302px;padding:4px 6px;font-size:12px;line-height:1.3;color:#000}
.hdr{text-align:center;padding:6px 0;border-bottom:2px solid #000}.hdr h1{font-size:16px;font-weight:900}
.onum{text-align:center;font-size:20px;font-weight:900;padding:6px 0;margin:4px 0;border:2px solid #000;background:#f0f0f0}
.badge{text-align:center;font-size:14px;font-weight:900;padding:4px;background:#000;color:#fff;letter-spacing:2px;margin-bottom:4px}
.nfo{padding:4px 0;border-bottom:1px solid #999;font-size:11px}.nfo div{display:flex;justify-content:space-between;margin:2px 0}.nfo b{font-weight:800}
.stit{font-weight:900;font-size:13px;padding:5px 0 3px;border-bottom:2px solid #000}
.itm{padding:4px 0;border-bottom:1px dotted #999}.ir{display:flex;align-items:center;gap:4px;font-size:12px}
.ir .n{font-weight:800;min-width:20px;font-size:14px}.ir .nm{flex:1;font-weight:700;font-size:13px}.ir .pr{font-weight:900;font-size:13px}
.split{display:flex;justify-content:space-between;font-size:13px;font-weight:800;padding:4px 0;border-top:1px dashed #999}
.tot{display:flex;justify-content:space-between;font-size:22px;font-weight:900;padding:6px 0;margin:4px 0;border-top:3px solid #000;border-bottom:3px solid #000}
.ft{text-align:center;padding:5px 0;margin-top:4px;border-top:1px dashed #999;font-size:10px}.ft b{font-size:12px;display:block;margin-bottom:2px}
@media print{body{width:80mm}@page{margin:0;size:80mm auto}}</style></head>
<body><div class="hdr"><h1>CENTRAL RESTAURANT</h1></div>
<div class="onum">MASA ${selectedTable} - KISI ${personNum}/${totalPersons}</div>
<div class="badge">ESIT BOLUNMUS HESAP</div>
<div class="nfo"><div><span>TARIH:</span><b>${now}</b></div>${currentWaiter ? `<div><span>GARSON:</span><b>${currentWaiter.name}</b></div>` : ''}</div>
<div class="stit">TUM URUNLER</div>${itemsHtml}
<div class="split"><span>Toplam</span><span>\u20AC${total.toFixed(2)}</span></div>
<div class="split"><span>${totalPersons} kisiye bolundu</span><span>\u20AC${total.toFixed(2)} / ${totalPersons}</span></div>
<div class="tot"><span>ODENECEK</span><span>\u20AC${perPerson.toFixed(2)}</span></div>
<div class="ft"><b>AFIYET OLSUN!</b>Central Hotel | +382 69 144 043</div></body></html>`;
    printHtmlContent(html);
  };

  const printHtmlContent = (html) => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    const tryPrint = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch(e) {} setTimeout(() => { try { document.body.removeChild(iframe); } catch(e) {} }, 3000); };
    if (iframe.contentWindow.document.readyState === 'complete') setTimeout(tryPrint, 300);
    else iframe.onload = () => setTimeout(tryPrint, 300);
  };

  const initSplitBill = () => {
    const allItems = tableDetailOrders.flatMap(o => o.items || []).map((item, idx) => ({ ...item, _splitIdx: idx }));
    setSplitGroups([allItems, []]);
    setShowSplit(true);
  };

  const moveItemToGroup = (itemIdx, fromGroup, toGroup) => {
    setSplitGroups(prev => {
      const next = prev.map(g => [...g]);
      const item = next[fromGroup].find(i => i._splitIdx === itemIdx);
      if (!item) return prev;
      next[fromGroup] = next[fromGroup].filter(i => i._splitIdx !== itemIdx);
      next[toGroup] = [...next[toGroup], item];
      return next;
    });
  };

  const getSplitTotal = (group) => group.reduce((s, i) => s + (i.price * i.quantity), 0);

  const addToCart = (item) => {
    setCart(prev => {
      const key = `${item.name}-${item.item_type}`;
      const step = item.item_type === 'beverage' ? 1 : 0.5;
      const existing = prev.find(c => `${c.name}-${c.item_type}` === key);
      if (existing) return prev.map(c => `${c.name}-${c.item_type}` === key ? { ...c, quantity: c.quantity + step } : c);
      return [...prev, { item_id: item.item_id || item.id || item.name, name: item.name, name_tr: item.name, price: item.price, quantity: 1, item_type: item.item_type || 'alakart', catering_selections: item.catering_selections || null }];
    });
  };

  const updateQty = (key, delta) => {
    setCart(prev => prev.map(c => {
      if (`${c.name}-${c.item_type}` !== key) return c;
      const step = c.item_type === 'beverage' ? 1 : 0.5;
      const min = c.item_type === 'beverage' ? 1 : 0.5;
      return { ...c, quantity: Math.max(min, c.quantity + delta * step) };
    }));
  };

  const removeFromCart = (key) => {
    setCart(prev => prev.filter(c => `${c.name}-${c.item_type}` !== key));
  };

  const getTotalCateringItems = () => Object.values(cateringQty).reduce((s, q) => s + q, 0);
  const calculateCateringPrice = () => calculateCateringTotal(cateringMenu, cateringQty);

  const addCateringToCart = () => {
    const total = getTotalCateringItems();
    if (total < 1) { toast.error('En az 1 urun secin'); return; }
    const price = calculateCateringPrice();
    const selections = {};
    for (const cat of CATERING_CATEGORIES) {
      const items = (cateringMenu?.[cat.key] || []).filter(item => (cateringQty[item.id] || 0) > 0);
      if (items.length > 0) selections[cat.key] = items.map(item => ({ id: item.id, name: item.name, quantity: cateringQty[item.id], price_addition: item.price_addition || 0 }));
    }
    setCart(prev => [...prev, {
      item_id: `catering-waiter-${Date.now()}`, name: `Gunluk Menu (${total} kap)`,
      name_tr: `Gunluk Menu (${total} kap)`,
      price, quantity: 1, item_type: 'catering', catering_selections: selections
    }]);
    toast.success(`Gunluk menu eklendi`);
    setCateringQty({});
  };

  const cartTotal = cart.reduce((s, c) => s + (Number(c.price) || 0) * (Number(c.quantity) || 0), 0);

  const handleSubmitOrder = async () => {
    if (!cart.length) { toast.error('Sepet bos'); return; }
    setLoading(true);
    try {
      const isPaket = orderMode === 'paket';
      await axios.post(`${API}/orders/waiter`, {
        items: cart,
        table_number: isPaket ? null : selectedTable,
        payment_method: payment,
        notes: notes || null,
        waiter_id: currentWaiter?.id || null,
        waiter_name: currentWaiter?.name || null,
        order_type: isPaket ? 'paket' : 'table',
        customer_phone: isPaket ? (paketPhone || null) : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isPaket ? 'Paket siparis gonderildi!' : `Masa ${selectedTable} siparisi gonderildi!`);
      setCart([]);
      setNotes('');
      setPaketPhone('');
      setCateringQty({});
      if (isPaket) {
        setOrderMode('table');
        setViewMode('tables');
      } else {
        fetchTableOrders();
        setViewMode('detail');
        await fetchTableDetail(selectedTable);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Siparis gonderilemedi');
    } finally { setLoading(false); }
  };

  if (!isAdmin) {
    return <div className="min-h-screen bg-bone flex items-center justify-center"><p className="text-charcoal/60 text-lg">Bu sayfaya erisim icin yetkiniz yok</p></div>;
  }

  // ========== PIN LOGIN VIEW ==========
  if (viewMode === 'login') {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="w-full max-w-xs">
          <div className="text-center mb-8">
            <h1 className="font-playfair text-3xl font-bold text-white mb-2">Garson Girisi</h1>
            <p className="text-stone-400">PIN kodunuzu girin</p>
          </div>
          <div className="bg-stone-800 rounded-2xl p-6 border border-stone-700">
            <div className="text-center mb-6">
              <div className="flex justify-center gap-3 mb-6">
                {[0,1,2,3].map(idx => (
                  <div key={`pin-dot-${idx}`} className={`w-4 h-4 rounded-full transition-all ${pinInput.length > idx ? 'bg-terracotta scale-110' : 'bg-stone-600'}`} />
                ))}
                {pinInput.length > 4 && [4,5].map(idx => (
                  <div key={`pin-dot-${idx}`} className={`w-4 h-4 rounded-full transition-all ${pinInput.length > idx ? 'bg-terracotta scale-110' : 'bg-stone-600'}`} />
                ))}
              </div>
              <Input
                type="password"
                value={pinInput}
                onChange={e => setPinInput(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handlePinLogin()}
                placeholder="PIN"
                className="bg-stone-700 border-stone-600 text-white text-center text-2xl tracking-[0.5em] placeholder:tracking-normal placeholder:text-sm"
                maxLength={6}
                autoFocus
                data-testid="waiter-pin-login"
              />
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[1,2,3,4,5,6,7,8,9,'',0,'del'].map((num) => (
                <button key={`pin-${num === '' ? 'blank' : num}`}
                  onClick={() => num === 'del' ? setPinInput(p => p.slice(0,-1)) : num !== '' && setPinInput(p => p.length < 6 ? p + num : p)}
                  className={`h-12 rounded-xl font-bold text-lg transition-all ${num === '' ? 'invisible' : num === 'del' ? 'bg-stone-700 text-stone-400 hover:bg-stone-600 text-sm' : 'bg-stone-700 text-white hover:bg-stone-600 active:scale-95'}`}
                  data-testid={`pin-key-${num}`}>
                  {num === 'del' ? 'Sil' : num}
                </button>
              ))}
            </div>
            <Button onClick={handlePinLogin} disabled={pinLoading || pinInput.length < 4}
              className="w-full bg-basil text-white hover:bg-basil/90 py-5 text-base font-semibold"
              data-testid="waiter-pin-submit">
              {pinLoading ? 'Kontrol ediliyor...' : 'Giris Yap'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ========== TABLE GRID VIEW ==========
  if (viewMode === 'tables') {
    const occupiedCount = Object.keys(tableOrders).length;
    const totalRevenue = Object.values(tableOrders).reduce((s, t) => s + (t?.total_price || 0), 0);
    const totalActiveOrders = Object.values(tableOrders).reduce((s, t) => s + (t?.orders?.length || 0), 0);

    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-900 to-stone-950 font-outfit">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold">Garson Paneli</span>
              <h1 className="font-playfair text-4xl font-bold text-white tracking-tight mt-1">Masa Yönetimi</h1>
              {currentWaiter && (
                <p className="text-stone-400 mt-1.5 text-sm">
                  Hoş geldin, <span className="text-terracotta font-medium">{currentWaiter.name}</span>
                </p>
              )}
            </div>
            {currentWaiter && (
              <button onClick={handleWaiterLogout}
                className="px-4 py-2 rounded-full bg-stone-800 text-stone-400 hover:bg-stone-700 hover:text-white text-sm transition-all"
                data-testid="waiter-logout-btn">
                Çıkış
              </button>
            )}
          </div>

          {/* Live KPI strip */}
          <div className="grid grid-cols-3 gap-3 mb-8" data-testid="waiter-kpi-strip">
            <div className="bg-gradient-to-br from-terracotta to-orange-700 rounded-2xl p-4 text-white">
              <p className="text-white/80 text-[10px] uppercase tracking-wider">Dolu Masa</p>
              <p className="font-playfair text-3xl font-bold mt-0.5">{occupiedCount}<span className="text-base text-white/70 ml-1">/ {TOTAL_TABLES}</span></p>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-700 rounded-2xl p-4 text-white">
              <p className="text-white/80 text-[10px] uppercase tracking-wider">Aktif Sipariş</p>
              <p className="font-playfair text-3xl font-bold mt-0.5">{totalActiveOrders}</p>
            </div>
            <div className="bg-gradient-to-br from-basil to-emerald-700 rounded-2xl p-4 text-white">
              <p className="text-white/80 text-[10px] uppercase tracking-wider">Açık Tutar</p>
              <p className="font-playfair text-3xl font-bold mt-0.5">€{totalRevenue.toFixed(2)}</p>
            </div>
          </div>

          {/* Waiter Calls Banner (pending bell requests from tables) */}
          <WaiterCallsBanner token={token} />

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
            {Array.from({ length: TOTAL_TABLES }, (_, i) => i + 1).map(num => {
              const tableData = tableOrders[num];
              const hasOrder = !!tableData;
              const orderCount = tableData?.orders?.length || 0;
              const totalPrice = tableData?.total_price || 0;
              // Detect channel of most recent order for accent dot
              const latestChannel = hasOrder ? (tableData.orders[0]?.channel || 'masa') : null;
              const channelDotColors = {
                garson: 'bg-amber-400',
                masa: 'bg-basil',
                salon: 'bg-blue-400',
                paket: 'bg-orange-400',
              };
              const channelLabels = {
                garson: 'Garson',
                masa: 'QR',
                salon: 'Salon',
                paket: 'Paket',
              };
              return (
                <motion.button
                  key={num}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleTableClick(num)}
                  className={`group aspect-square rounded-2xl flex flex-col items-center justify-center gap-0.5 transition-all ring-1 relative overflow-hidden ${
                    hasOrder
                      ? 'bg-gradient-to-br from-terracotta/30 via-orange-700/20 to-terracotta/5 ring-terracotta/80 text-terracotta shadow-xl shadow-terracotta/20'
                      : 'bg-gradient-to-br from-stone-800 to-stone-900 ring-stone-700 text-stone-400 hover:ring-basil/70 hover:text-basil hover:from-stone-800 hover:to-basil/10'
                  }`}
                  data-testid={`waiter-table-${num}`}
                >
                  {/* Decorative glow accent on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity ${
                    hasOrder ? 'from-terracotta/10 to-transparent' : 'from-basil/5 to-transparent'
                  }`} />
                  {hasOrder && (
                    <>
                      <span className="absolute top-2 right-2 flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-terracotta opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-terracotta" />
                      </span>
                      <span className="absolute top-1.5 left-2 text-[9px] uppercase tracking-widest opacity-50 font-bold">Masa</span>
                    </>
                  )}
                  <span className="font-playfair text-3xl md:text-4xl font-bold relative z-10">{num}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider relative z-10 ${hasOrder ? 'opacity-90' : 'opacity-60 group-hover:opacity-90'}`}>{hasOrder ? 'Dolu' : 'Boş'}</span>
                  {hasOrder && <span className="text-[10px] font-medium mt-0.5 opacity-80 relative z-10">{orderCount} sipariş</span>}
                  {hasOrder && <span className="text-sm font-bold mt-0.5 relative z-10">€{totalPrice.toFixed(2)}</span>}
                  {hasOrder && latestChannel && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 z-10" data-testid={`table-channel-${num}`}>
                      <span className={`w-2 h-2 rounded-full ${channelDotColors[latestChannel] || 'bg-stone-400'} shadow-md ring-2 ring-charcoal/40`} />
                      <span className="text-[9px] font-bold uppercase tracking-wider opacity-70">{channelLabels[latestChannel] || ''}</span>
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
          {/* Paket Siparis Butonu */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setOrderMode('paket');
              setSelectedTable(null);
              setCart([]);
              setNotes('');
              setPaketPhone('');
              setViewMode('neworder');
            }}
            className="mt-6 w-full py-5 rounded-2xl flex items-center justify-center gap-3 text-xl font-bold bg-amber-600/20 border-2 border-amber-500 text-amber-400 hover:bg-amber-600/30 transition-all"
            data-testid="paket-siparis-btn"
          >
            <Package className="w-7 h-7" />
            Paket Siparis
          </motion.button>
        </div>
      </div>
    );
  }

  // ========== TABLE DETAIL VIEW (existing orders) ==========
  if (viewMode === 'detail') {
    return (
      <div className="min-h-screen bg-stone-900">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <button onClick={handleBack} className="p-2 rounded-full bg-stone-800 text-stone-300 hover:bg-stone-700" data-testid="waiter-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-white text-xl font-bold">Masa {selectedTable}</h1>
              <p className="text-stone-400 text-sm">{tableDetailOrders.length} aktif siparis</p>
            </div>
            <div className="bg-terracotta/20 px-5 py-2 rounded-full">
              <span className="text-terracotta font-bold text-lg" data-testid="waiter-table-detail-total">{'\u20AC'}{tableDetailTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Existing Orders */}
          <div className="space-y-3 mb-6">
            {tableDetailOrders.map((order, idx) => (
              <div key={order.id} className="bg-stone-800 rounded-xl p-4 border border-stone-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-stone-500" />
                    <span className="text-stone-400 text-xs">Siparis #{idx + 1}</span>
                    <span className="text-stone-500 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(order.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <span className="text-terracotta font-bold text-sm">{'\u20AC'}{order.total_price?.toFixed(2)}</span>
                </div>
                <div className="space-y-1">
                  {order.items?.map((item, i) => (
                    <div key={`${order.id}-${item.name}-${i}`} className="flex justify-between text-sm">
                      <span className="text-stone-300">{item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)}x {item.name}</span>
                      <span className="text-stone-400">{'\u20AC'}{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {order.notes && <p className="text-stone-500 text-xs mt-2 italic">Not: {order.notes}</p>}
              </div>
            ))}
            {tableDetailOrders.length === 0 && (
              <div className="text-center py-8 text-stone-500">Bu masada aktif siparis yok</div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Button
              onClick={() => setViewMode('neworder')}
              className="bg-basil text-white hover:bg-basil/90 py-4 text-sm font-semibold"
              data-testid="waiter-add-order-btn"
            >
              <Plus className="w-4 h-4 mr-1" />Yeni Siparis
            </Button>
            <Button
              onClick={printReceipt}
              disabled={tableDetailOrders.length === 0}
              className="bg-stone-700 text-white hover:bg-stone-600 py-4 text-sm font-semibold"
              data-testid="waiter-print-receipt-btn"
            >
              <Printer className="w-4 h-4 mr-1" />Yazdir
            </Button>
            <Button
              onClick={() => setShowTransfer(true)}
              disabled={tableDetailOrders.length === 0}
              className="bg-blue-600 text-white hover:bg-blue-500 py-4 text-sm font-semibold"
              data-testid="waiter-transfer-btn"
            >
              <ArrowRightLeft className="w-4 h-4 mr-1" />Transfer
            </Button>
            <Button
              onClick={handleCloseTable}
              disabled={closingTable || tableDetailOrders.length === 0}
              className="bg-terracotta text-white hover:bg-terracotta/90 py-4 text-sm font-semibold"
              data-testid="waiter-close-table-btn"
            >
              {closingTable ? 'Kapatiliyor...' : <><Receipt className="w-4 h-4 mr-1" />Hesap Kes</>}
            </Button>
          </div>

          {/* Split Bill Button */}
          {tableDetailOrders.length > 0 && (
            <Button
              onClick={initSplitBill}
              variant="outline"
              className="w-full border-stone-600 text-stone-300 hover:bg-stone-800 py-3"
              data-testid="waiter-split-bill-btn"
            >
              <Scissors className="w-4 h-4 mr-2" />Hesabi Bol (Split Bill)
            </Button>
          )}

          {/* Bill Summary */}
          {tableDetailOrders.length > 0 && (
            <div className="mt-4 bg-stone-800/50 border border-stone-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="w-5 h-5 text-terracotta" />
                <span className="text-white font-semibold">Adisyon</span>
              </div>
              <div className="space-y-1 text-sm">
                {tableDetailOrders.flatMap(o => o.items || []).reduce((acc, item) => {
                  const key = item.name;
                  const existing = acc.find(a => a.name === key);
                  if (existing) { existing.quantity += item.quantity; existing.total += item.price * item.quantity; }
                  else acc.push({ name: key, quantity: item.quantity, price: item.price, total: item.price * item.quantity });
                  return acc;
                }, []).map((item) => (
                  <div key={`bill-${item.name}`} className="flex justify-between">
                    <span className="text-stone-300">{item.quantity}x {item.name}</span>
                    <span className="text-stone-400">{'\u20AC'}{item.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-stone-700 mt-3 pt-3 flex justify-between">
                <span className="text-white font-bold">TOPLAM</span>
                <span className="text-terracotta font-bold text-lg">{'\u20AC'}{tableDetailTotal.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Transfer Dialog */}
          <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
            <DialogContent className="bg-stone-900 border-stone-700 max-w-md">
              <DialogHeader><DialogTitle className="text-white">Masa Transferi</DialogTitle></DialogHeader>
              <p className="text-stone-400 text-sm mb-3">Masa {selectedTable} siparislerini hangi masaya tasimak istiyorsunuz?</p>
              <div className="grid grid-cols-4 gap-2">
                {Array.from({ length: TOTAL_TABLES }, (_, i) => i + 1).filter(n => n !== selectedTable).map(num => {
                  const isOccupied = !!tableOrders[num];
                  return (
                    <button key={num} onClick={() => !isOccupied && handleTransfer(num)} disabled={isOccupied || transferring}
                      className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-bold border-2 transition-all ${
                        isOccupied ? 'bg-stone-800 border-stone-700 text-stone-600 cursor-not-allowed opacity-50' : 'bg-stone-800 border-stone-600 text-stone-300 hover:border-blue-500 hover:text-blue-400'
                      }`}
                      data-testid={`transfer-table-${num}`}
                    >
                      <span className="text-lg">{num}</span>
                      <span className="text-[10px] opacity-60">{isOccupied ? 'Dolu' : 'Bos'}</span>
                    </button>
                  );
                })}
              </div>
              {transferring && <p className="text-blue-400 text-sm text-center mt-2">Transfer ediliyor...</p>}
            </DialogContent>
          </Dialog>

          {/* Split Bill Dialog */}
          <Dialog open={showSplit} onOpenChange={setShowSplit}>
            <DialogContent className="bg-stone-900 border-stone-700 max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-white">Hesabi Bol - Masa {selectedTable}</DialogTitle></DialogHeader>
              
              {/* Mode Selector */}
              <div className="flex gap-2 mb-4">
                <button onClick={() => setSplitMode('item')} data-testid="split-mode-item"
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${splitMode === 'item' ? 'bg-terracotta text-white' : 'bg-stone-800 text-stone-400 hover:bg-stone-700'}`}>
                  <Scissors className="w-4 h-4 inline mr-1" />Urun Bazli
                </button>
                <button onClick={() => setSplitMode('equal')} data-testid="split-mode-equal"
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${splitMode === 'equal' ? 'bg-blue-600 text-white' : 'bg-stone-800 text-stone-400 hover:bg-stone-700'}`}>
                  <ArrowRightLeft className="w-4 h-4 inline mr-1" />Esit Bol
                </button>
              </div>

              {splitMode === 'equal' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <button onClick={() => setSplitPersonCount(Math.max(2, splitPersonCount - 1))} className="w-10 h-10 rounded-full bg-stone-800 text-white flex items-center justify-center hover:bg-stone-700" data-testid="split-decrease">
                      <Minus className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                      <span className="text-4xl font-bold text-white">{splitPersonCount}</span>
                      <p className="text-stone-400 text-sm">Kisi</p>
                    </div>
                    <button onClick={() => setSplitPersonCount(splitPersonCount + 1)} className="w-10 h-10 rounded-full bg-stone-800 text-white flex items-center justify-center hover:bg-stone-700" data-testid="split-increase">
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="bg-stone-800 rounded-xl p-4 text-center">
                    <p className="text-stone-400 text-sm mb-1">Toplam: {'\u20AC'}{tableDetailTotal.toFixed(2)}</p>
                    <p className="text-3xl font-bold text-terracotta">
                      {'\u20AC'}{(tableDetailTotal / splitPersonCount).toFixed(2)}
                      <span className="text-sm text-stone-400 font-normal ml-1">/ kisi</span>
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {Array.from({ length: splitPersonCount }, (_, i) => (
                      <Button key={`equal-split-${i}`}
                        onClick={() => { printEqualSplitReceipt(i + 1, splitPersonCount, tableDetailTotal); toast.success(`Hesap ${i + 1} yazdiriliyor...`); }}
                        className="bg-blue-600 hover:bg-blue-500 text-white text-xs py-3"
                        data-testid={`equal-split-print-${i}`}>
                        <Printer className="w-3 h-3 mr-1" />Hesap {i + 1}: {'\u20AC'}{(tableDetailTotal / splitPersonCount).toFixed(2)}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-stone-400 text-sm mb-4">Urunleri gruplara ayirmak icin oklara tiklayin</p>
                  <div className="grid grid-cols-2 gap-4">
                    {splitGroups.map((group, gi) => (
                      <div key={gi} className={`rounded-xl border-2 p-3 ${gi === 0 ? 'border-blue-500/50 bg-blue-500/5' : 'border-terracotta/50 bg-terracotta/5'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`font-bold text-sm ${gi === 0 ? 'text-blue-400' : 'text-terracotta'}`}>Hesap {gi + 1}</span>
                          <span className={`font-bold ${gi === 0 ? 'text-blue-400' : 'text-terracotta'}`}>{'\u20AC'}{getSplitTotal(group).toFixed(2)}</span>
                        </div>
                        <div className="space-y-1 min-h-[80px]">
                          {group.map(item => (
                            <div key={item._splitIdx} className="flex items-center justify-between bg-stone-800 rounded-lg px-2 py-1.5 text-sm">
                              <span className="text-stone-300 flex-1 truncate">{item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)}x {item.name}</span>
                              <span className="text-stone-400 text-xs mr-2">{'\u20AC'}{(item.price * item.quantity).toFixed(2)}</span>
                              <button onClick={() => moveItemToGroup(item._splitIdx, gi, gi === 0 ? 1 : 0)}
                                className={`w-6 h-6 rounded-full flex items-center justify-center ${gi === 0 ? 'bg-terracotta/20 text-terracotta hover:bg-terracotta/30' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`}
                                data-testid={`split-move-${item._splitIdx}`}>
                                <ArrowRightLeft className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          {group.length === 0 && <p className="text-stone-600 text-xs text-center py-4">Urun ekleyin</p>}
                        </div>
                        {group.length > 0 && (
                          <Button onClick={() => { printSplitReceipt(group, gi, getSplitTotal(group)); toast.success(`Hesap ${gi + 1} yazdiriliyor...`); }}
                            size="sm" className={`w-full mt-2 ${gi === 0 ? 'bg-blue-600 hover:bg-blue-500' : 'bg-terracotta hover:bg-terracotta/90'} text-white text-xs`}
                            data-testid={`split-print-${gi}`}>
                            <Printer className="w-3 h-3 mr-1" />Hesap {gi + 1} Yazdir ({'\u20AC'}{getSplitTotal(group).toFixed(2)})
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              
              <div className="border-t border-stone-700 pt-3 mt-3 flex justify-between items-center">
                <span className="text-stone-400 text-sm">Toplam: {'\u20AC'}{tableDetailTotal.toFixed(2)}</span>
                <Button onClick={() => setShowSplit(false)} variant="outline" className="border-stone-600 text-stone-300">Kapat</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  // ========== NEW ORDER VIEW ==========
  const filtered = menuItems.filter(m => !search || m.name.toLowerCase().includes(search.toLowerCase()));
  const categories = [...new Set(filtered.map(m => m.category))];
  const filteredBev = beverageItems.filter(b => !search || b.name.toLowerCase().includes(search.toLowerCase()));
  const bevCats = [...new Set(filteredBev.map(b => b.category))];
  const cateringByCategory = CATERING_CATEGORIES.map(cat => ({
    ...cat, items: (cateringMenu?.[cat.key] || []).filter(item => !search || item.name.toLowerCase().includes(search.toLowerCase()))
  })).filter(cat => cat.items.length > 0);
  const cateringTotal = getTotalCateringItems();

  return (
    <div className="min-h-screen bg-stone-900">
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <button onClick={handleBack} className="p-2 rounded-full bg-stone-800 text-stone-300 hover:bg-stone-700" data-testid="waiter-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-white text-xl font-bold flex items-center gap-2">
              {orderMode === 'paket' ? <><Package className="w-5 h-5 text-amber-400" />Paket Siparis</> : <>Masa {selectedTable} - Yeni Siparis</>}
            </h1>
            <p className="text-stone-400 text-sm">{currentWaiter ? currentWaiter.name : 'Urun secin'}</p>
          </div>
          <div className="bg-terracotta/20 px-4 py-2 rounded-full">
            <span className="text-terracotta font-bold" data-testid="waiter-cart-total">{'\u20AC'}{cartTotal.toFixed(2)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Menu */}
          <div className="lg:col-span-2 space-y-3">
            {/* Tabs */}
            <div className="flex gap-2">
              {[
                { id: 'alakart', icon: UtensilsCrossed, label: 'A La Carte' },
                { id: 'beverages', icon: Coffee, label: 'Icecekler' },
                { id: 'catering', icon: ChefHat, label: 'Gunluk Menu' },
              ].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${activeTab === tab.id ? 'bg-basil text-white' : 'bg-stone-800 text-stone-400 hover:bg-stone-700'}`}
                  data-testid={`waiter-tab-${tab.id}`}>
                  <tab.icon className="w-4 h-4" />{tab.label}
                </button>
              ))}
            </div>

            <Input placeholder="Urun ara..." value={search} onChange={e => setSearch(e.target.value)}
              className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500" data-testid="waiter-search" />

            <div className="max-h-[calc(100vh-220px)] overflow-y-auto space-y-3 pr-1">
              {/* A La Carte */}
              {activeTab === 'alakart' && categories.map(cat => (
                <div key={cat}>
                  <h3 className="font-semibold text-xs text-stone-500 uppercase tracking-wide mb-2">{cat}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {filtered.filter(m => m.category === cat).map(item => (
                      <button key={item.name} onClick={() => addToCart({ ...item, item_type: 'alakart', item_id: item.id })}
                        className="bg-stone-800 rounded-xl p-3 text-left hover:bg-stone-700 transition-all active:scale-95 border border-stone-700"
                        data-testid={`waiter-item-${item.name.replace(/\s+/g, '-')}`}>
                        <p className="font-semibold text-white text-sm truncate">{item.name}</p>
                        <p className="text-terracotta font-bold text-sm mt-1">{'\u20AC'}{item.price.toFixed(2)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Beverages */}
              {activeTab === 'beverages' && bevCats.map(cat => (
                <div key={cat}>
                  <h3 className="font-semibold text-xs text-stone-500 uppercase tracking-wide mb-2">{cat}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {filteredBev.filter(b => b.category === cat).map(item => (
                      <button key={item.id} onClick={() => addToCart({ ...item, item_type: 'beverage', item_id: item.id })}
                        className="bg-stone-800 rounded-xl p-3 text-left hover:bg-stone-700 transition-all active:scale-95 border border-stone-700"
                        data-testid={`waiter-bev-${item.id}`}>
                        <p className="font-semibold text-white text-sm truncate">{item.name}</p>
                        <p className="text-terracotta font-bold text-sm mt-1">{'\u20AC'}{item.price.toFixed(2)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Catering */}
              {activeTab === 'catering' && (
                <>
                  {!cateringMenu ? (
                    <div className="text-center py-12"><ChefHat className="w-12 h-12 text-stone-600 mx-auto mb-3" /><p className="text-stone-500">Bugun icin gunluk menu yok</p></div>
                  ) : (
                    <>
                      <div className="bg-stone-800 border border-stone-700 rounded-xl p-3 text-sm text-stone-300">
                        Her 4 kategoriden 1er = {'\u20AC'}{BASE_PRICE} | 2ser = {'\u20AC'}{BASE_PRICE * 2}
                      </div>
                      {cateringByCategory.map(cat => (
                        <div key={cat.key}>
                          <h3 className="font-semibold text-xs text-stone-500 uppercase tracking-wide mb-2">{cat.label}</h3>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {cat.items.map(item => {
                              const qty = cateringQty[item.id] || 0;
                              return (
                                <div key={item.id} className={`bg-stone-800 rounded-xl p-3 border transition-all ${qty > 0 ? 'border-terracotta' : 'border-stone-700'}`}>
                                  <p className="font-semibold text-white text-sm truncate">{item.name}</p>
                                  <p className="text-xs text-stone-500">{item.price_addition > 0 ? `+${'\u20AC'}${item.price_addition}` : 'Set dahil'}</p>
                                  <div className="flex items-center justify-center gap-2 mt-2">
                                    <button onClick={() => setCateringQty(p => ({ ...p, [item.id]: Math.max(0, qty - 1) }))} disabled={qty === 0}
                                      className="w-7 h-7 rounded-full bg-stone-700 flex items-center justify-center text-white disabled:opacity-30"><Minus className="w-3 h-3" /></button>
                                    <span className="w-6 text-center font-bold text-white text-sm">{qty}</span>
                                    <button onClick={() => setCateringQty(p => ({ ...p, [item.id]: qty + 1 }))}
                                      className="w-7 h-7 rounded-full bg-terracotta/30 flex items-center justify-center text-terracotta"><Plus className="w-3 h-3" /></button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {cateringTotal > 0 && (
                        <div className="bg-stone-800 rounded-xl p-4 border border-terracotta/30 flex items-center justify-between">
                          <div>
                            <span className="text-white font-bold">{cateringTotal} kap {'\u2014'} {'\u20AC'}{calculateCateringPrice().toFixed(2)}</span>
                          </div>
                          <Button onClick={addCateringToCart} className="bg-terracotta text-white hover:bg-terracotta/90 rounded-full" data-testid="waiter-add-catering">
                            <Plus className="w-4 h-4 mr-1" />Sepete Ekle
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right: Cart */}
          <div className="lg:col-span-1">
            <div className="bg-stone-800 rounded-2xl p-4 sticky top-4 border border-stone-700">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-stone-700">
                <ShoppingCart className="w-5 h-5 text-terracotta" />
                <h3 className="font-semibold text-white">Masa {selectedTable} Sepeti</h3>
                <span className="ml-auto text-stone-400 text-sm">{cart.length} urun</span>
              </div>

              {cart.length === 0 ? (
                <p className="text-stone-500 text-center py-8 text-sm">Urun ekleyin</p>
              ) : (
                <div className="space-y-1 max-h-[250px] overflow-y-auto mb-3">
                  {cart.map(c => {
                    const key = `${c.name}-${c.item_type}`;
                    const price = Number(c.price) || 0;
                    const qty = Number(c.quantity) || 0;
                    return (
                      <div key={key} className="flex items-center gap-2 py-2 border-b border-stone-700/50 last:border-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{c.name}</p>
                          <p className="text-xs text-stone-500">{'\u20AC'}{price.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => updateQty(key, -1)} className="w-6 h-6 rounded-full bg-stone-700 flex items-center justify-center"><Minus className="w-3 h-3 text-white" /></button>
                          <span className="w-5 text-center font-bold text-white text-sm">{qty % 1 === 0 ? qty : qty.toFixed(1)}</span>
                          <button onClick={() => updateQty(key, 1)} className="w-6 h-6 rounded-full bg-stone-700 flex items-center justify-center"><Plus className="w-3 h-3 text-white" /></button>
                        </div>
                        <span className="font-bold text-sm text-white w-14 text-right">{'\u20AC'}{(price * qty).toFixed(2)}</span>
                        <button onClick={() => removeFromCart(key)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="border-t border-stone-700 pt-3 space-y-3">
                <div className="flex justify-between font-bold text-lg">
                  <span className="text-white">Toplam</span>
                  <span className="text-terracotta">{'\u20AC'}{cartTotal.toFixed(2)}</span>
                </div>

                <div className="relative">
                  <StickyNote className="absolute left-3 top-3 w-4 h-4 text-stone-500" />
                  <Textarea placeholder="Siparis notu..." value={notes} onChange={e => setNotes(e.target.value)}
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500 pl-9 min-h-[60px]"
                    data-testid="waiter-notes" />
                </div>

                {orderMode === 'paket' && (
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-4 h-4 text-amber-500" />
                    <Input
                      placeholder="Telefon numarasi (fis QR icin)"
                      value={paketPhone}
                      onChange={e => setPaketPhone(e.target.value)}
                      className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500 pl-9"
                      data-testid="waiter-paket-phone"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setPayment('nakit')}
                    className={`flex items-center justify-center gap-2 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${payment === 'nakit' ? 'border-green-500 bg-green-500/10 text-green-400' : 'border-stone-600 text-stone-400'}`}
                    data-testid="waiter-pay-cash"><Banknote className="w-4 h-4" />Nakit</button>
                  <button onClick={() => setPayment('kart')}
                    className={`flex items-center justify-center gap-2 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${payment === 'kart' ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-stone-600 text-stone-400'}`}
                    data-testid="waiter-pay-card"><CreditCard className="w-4 h-4" />Kart</button>
                </div>

                <Button onClick={handleSubmitOrder} disabled={loading || !cart.length}
                  className="w-full bg-basil text-white hover:bg-basil/90 py-5 text-base font-semibold"
                  data-testid="waiter-submit-btn">
                  {loading ? 'Gonderiliyor...' : <><Send className="w-5 h-5 mr-2" />Siparisi Gonder</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Waiter;
